import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

export const FeesPage = ({ user, t, formatCurrency, buildingId = null }) => {
  const navigate = useNavigate();
  const [cumulativeData, setCumulativeData] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInitCarryoverModal, setShowInitCarryoverModal] = useState(false);
  const [showOtherPaymentModal, setShowOtherPaymentModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [carryoverInitialized, setCarryoverInitialized] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0]
  });
  const [formData, setFormData] = useState({ 
    apartment_id: '', 
    amount: 500, 
    month: new Date().getMonth() + 1, 
    year: new Date().getFullYear() 
  });
  const [generateFormData, setGenerateFormData] = useState({ 
    apartment_id: '', 
    amount: 500, 
    year: new Date().getFullYear() 
  });
  const [initCarryoverData, setInitCarryoverData] = useState({
    effective_year: new Date().getFullYear(),
    initial_amount: 0
  });
  const [otherPaymentFormData, setOtherPaymentFormData] = useState({
    apartment_id: '',
    description: '',
    amount_per_month: '',
    start_month: new Date().getMonth() + 1,
    start_year: new Date().getFullYear(),
    duration_months: 1
  });
  
  // Current date info for determining eligible months
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-based
  
  // Filters - default to current year and first apartment
  const [filterApartment, setFilterApartment] = useState(null);
  const [filterYear, setFilterYear] = useState(currentYear.toString());
  const [apartmentsLoaded, setApartmentsLoaded] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin') {
      navigate('/app');
      return;
    }
    fetchApartments();
  }, [user, navigate, buildingId]);

  // Set default apartment to first apartment when apartments are loaded
  useEffect(() => {
    if (apartments.length > 0 && !apartmentsLoaded) {
      const sortedApartments = [...apartments].sort((a, b) => {
        const numA = parseInt(a.unit_number) || 0;
        const numB = parseInt(b.unit_number) || 0;
        return numA - numB;
      });
      setFilterApartment(sortedApartments[0].id);
      setApartmentsLoaded(true);
    }
  }, [apartments, apartmentsLoaded]);

  // Fetch cumulative data whenever filters change
  useEffect(() => {
    if (filterApartment && filterYear) {
      fetchCumulativeData();
    }
  }, [filterYear, filterApartment, buildingId]);

  const fetchApartments = async () => {
    try {
      const aptsUrl = buildingId ? `${API}/apartments?building_id=${buildingId}` : `${API}/apartments`;
      const aptsRes = await axios.get(aptsUrl, { withCredentials: true });
      setApartments(aptsRes.data);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    }
  };

  const fetchCumulativeData = async () => {
    try {
      setLoading(true);
      const year = parseInt(filterYear) || currentYear;
      const apartment = filterApartment;
      
      let url = `${API}/maintenance-fees/cumulative?year=${year}`;
      if (apartment) url += `&apartment_id=${apartment}`;
      if (buildingId) url += `&building_id=${buildingId}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setCumulativeData(response.data);
      
      // Check if carryover is initialized for current apartment
      if (response.data.carryover_info && apartment) {
        const carryoverInfo = response.data.carryover_info[apartment];
        setCarryoverInitialized(carryoverInfo?.is_initialized || false);
      }
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    await fetchCumulativeData();
  };

  // Check if a month is in the future (for UI hints only, not for disabling)
  const isFutureMonth = (month, year) => {
    const selectedYear = parseInt(year);
    if (selectedYear < currentYear) return false;
    if (selectedYear > currentYear) return true;
    return month > currentMonth;
  };

  // Build the complete 13-row data (Devir + 12 months) with calculated cumulative balance
  // Using the new formula: Kalan = Devir + Ödenen - Borç
  const tableRows = useMemo(() => {
    if (!cumulativeData?.fees) return [];
    
    // First pass: build base data
    const rows = cumulativeData.fees.map(fee => {
      const isDevir = fee.type === 'opening_balance';
      const isFuture = !isDevir && isFutureMonth(fee.month, fee.year);
      
      // Debt amounts for this month
      const aidatDebt = fee.amount || 0;
      const otherPaymentDebt = fee.other_payment_debt || 0;
      const totalMonthlyDebt = aidatDebt + otherPaymentDebt;
      
      // "Ödenen Tutar" - Payments made IN this month (by payment_date)
      // This is different from payments applied TO this month's fees
      const paymentsInMonth = fee.payments_in_month || 0;
      
      return {
        ...fee,
        isDevir,
        isFuture,
        isVirtual: fee.id?.startsWith('virtual_'),
        hasAdvancePayments: fee.has_advance_payments || false,
        advanceAmount: fee.advance_amount || 0,
        // Store calculated values
        aidatDebt,
        otherPaymentDebt,
        totalMonthlyDebt,
        // "Ödenen Tutar" = payments made IN this month
        paymentsInMonth
      };
    });
    
    // Second pass: calculate cumulative balance using the formula
    // Kalan = Devir (previous month's Kalan) + Ödenen Tutar - (Aidat + Diğer Ödeme)
    // Where "Ödenen Tutar" is payments made IN that month (paymentsInMonth)
    let runningCumulative = 0;
    
    rows.forEach((row, index) => {
      if (row.isDevir) {
        // For Devir row: use the opening balance directly
        // Backend stores cumulative_balance as debt=positive, credit=negative
        // We want to display: debt=negative (they owe), credit=positive (overpaid)
        runningCumulative = -(row.cumulative_balance || 0);
        row.calculatedCumulative = runningCumulative;
      } else {
        // For regular months: Kalan = Previous Kalan + Paid IN Month - Debt
        const prevCumulative = runningCumulative;
        runningCumulative = prevCumulative + row.paymentsInMonth - row.totalMonthlyDebt;
        row.calculatedCumulative = runningCumulative;
      }
    });
    
    return rows;
  }, [cumulativeData, currentYear, currentMonth]);

  // Calculate summary values - column totals for the footer
  const summaryData = useMemo(() => {
    if (tableRows.length === 0) return { 
      totalAidat: 0, 
      totalDiger: 0, 
      totalOdenen: 0,
      endBalance: 0 
    };
    
    // Sum up column values (excluding Devir row)
    let totalAidat = 0;
    let totalDiger = 0;
    let totalOdenen = 0;
    
    tableRows.forEach(row => {
      if (!row.isDevir) {
        totalAidat += row.aidatDebt || 0;
        totalDiger += row.otherPaymentDebt || 0;
        totalOdenen += row.paymentsInMonth || 0;
      }
    });
    
    // Get the last row's calculated cumulative balance
    const lastRow = tableRows[tableRows.length - 1];
    const endBalance = lastRow?.calculatedCumulative || 0;
    
    return {
      totalAidat,
      totalDiger,
      totalOdenen,
      endBalance
    };
  }, [tableRows]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate month is not in the future
    const selectedYear = formData.year;
    const selectedMonth = formData.month;
    
    if (selectedYear > currentYear || (selectedYear === currentYear && selectedMonth > currentMonth)) {
      toast.error(t('cannotCreateFutureFees'));
      return;
    }
    
    try {
      const url = buildingId ? `${API}/maintenance-fees?building_id=${buildingId}` : `${API}/maintenance-fees`;
      await axios.post(url, formData, { withCredentials: true });
      toast.success(t('feeCreated'));
      setShowModal(false);
      setFormData({ apartment_id: '', amount: 500, month: new Date().getMonth() + 1, year: new Date().getFullYear() });
      await fetchData();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  // Open simplified payment modal
  const handleOpenPaymentModal = (fee) => {
    const paidTotal = fee.paid_amount_total || 0;
    const debtAmount = fee.amount || 0;
    const remaining = debtAmount - paidTotal;
    
    setSelectedFee(fee);
    setPaymentFormData({
      // For future months (no debt), default amount can be any value
      // For regular months with remaining debt, default to remaining amount
      amount: remaining > 0 ? remaining.toFixed(2) : '',
      payment_date: new Date().toISOString().split('T')[0]
    });
    setShowPaymentModal(true);
  };

  // Handle payment submission - now supports both regular and advance payments
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFee) return;
    
    const amount = parseFloat(paymentFormData.amount);
    const paidTotal = selectedFee.paid_amount_total || 0;
    const debtAmount = selectedFee.amount || 0;
    const remaining = debtAmount - paidTotal;
    const isVirtual = selectedFee.isVirtual || selectedFee.id?.startsWith('virtual_');
    const isFutureMonth = selectedFee.isFuture;
    
    if (amount <= 0) {
      toast.error(t('amountRequired'));
      return;
    }
    
    // For regular fees with debt, check if payment exceeds remaining
    if (!isVirtual && !isFutureMonth && debtAmount > 0 && amount > remaining + 0.01) {
      toast.error(t('paymentExceedsRemaining'));
      return;
    }
    
    try {
      // Determine if this is an advance payment (virtual/future month) or regular payment
      if (isVirtual || (isFutureMonth && debtAmount === 0)) {
        // Create advance payment allocation
        const url = buildingId 
          ? `${API}/advance-payments?building_id=${buildingId}`
          : `${API}/advance-payments`;
        
        await axios.post(url, {
          apartment_id: selectedFee.apartment_id,
          target_year: selectedFee.year,
          target_month: selectedFee.month,
          amount: amount,
          payment_date: paymentFormData.payment_date
        }, { withCredentials: true });
        
        toast.success(t('advancePaymentAllocated'));
      } else {
        // Regular payment to existing fee
        const url = buildingId 
          ? `${API}/maintenance-fees/${selectedFee.id}/payments?building_id=${buildingId}`
          : `${API}/maintenance-fees/${selectedFee.id}/payments`;
        
        await axios.post(url, {
          amount: amount,
          payment_date: paymentFormData.payment_date,
          method: '',
          note: ''
        }, { withCredentials: true });
        
        toast.success(t('paymentRecorded'));
      }
      
      setShowPaymentModal(false);
      setSelectedFee(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  // Undo/Reset all payments for a fee - now handles both regular and advance payments
  const handleUndoPayment = async (fee) => {
    if (!window.confirm(t('undoPaymentConfirm'))) return;
    
    const isVirtual = fee.isVirtual || fee.id?.startsWith('virtual_');
    
    try {
      if (isVirtual) {
        // Delete advance payment allocations for this period
        const url = buildingId 
          ? `${API}/advance-payments/reset/${fee.apartment_id}/${fee.year}/${fee.month}?building_id=${buildingId}`
          : `${API}/advance-payments/reset/${fee.apartment_id}/${fee.year}/${fee.month}`;
        
        await axios.delete(url, { withCredentials: true });
        toast.success(t('advancePaymentDeleted'));
      } else {
        // Reset regular payment transactions
        const url = buildingId 
          ? `${API}/maintenance-fees/${fee.id}/reset-payments?building_id=${buildingId}`
          : `${API}/maintenance-fees/${fee.id}/reset-payments`;
        
        await axios.delete(url, { withCredentials: true });
        toast.success(t('paymentUndone'));
      }
      
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  // Initialize carryover (one-time)
  const handleInitCarryover = async (e) => {
    e.preventDefault();
    try {
      const url = buildingId 
        ? `${API}/initial-carryover?building_id=${buildingId}`
        : `${API}/initial-carryover`;
      
      await axios.post(url, {
        apartment_id: filterApartment,
        effective_year: initCarryoverData.effective_year,
        initial_amount: parseFloat(initCarryoverData.initial_amount)
      }, { withCredentials: true });
      
      toast.success(t('carryoverInitialized'));
      setShowInitCarryoverModal(false);
      setCarryoverInitialized(true);
      setInitCarryoverData({ effective_year: currentYear, initial_amount: 0 });
      await fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || t('operationFailed');
      if (detail.includes('already set')) {
        toast.error(t('carryoverAlreadySet'));
      } else {
        toast.error(detail);
      }
    }
  };

  const handleGenerateYearlyFees = async (e) => {
    e.preventDefault();
    try {
      const url = buildingId 
        ? `${API}/maintenance-fees/generate-yearly?building_id=${buildingId}`
        : `${API}/maintenance-fees/generate-yearly`;
      const response = await axios.post(url, generateFormData, { withCredentials: true });
      const { created_count, skipped_count, future_months_not_created } = response.data;
      
      if (created_count > 0) {
        let message = `${t('yearlyFeesGenerated')} (${created_count} ${t('created')})`;
        if (future_months_not_created > 0) {
          message += ` - ${future_months_not_created} ${t('futureMonthsNotCreated')}`;
        }
        toast.success(message);
      } else if (skipped_count > 0) {
        toast.warning(t('allFeesAlreadyExist'));
      } else {
        toast.info(`${created_count} ${t('created')}, ${skipped_count} ${t('skipped')}`);
      }
      
      setShowGenerateModal(false);
      setGenerateFormData({ apartment_id: '', amount: 500, year: new Date().getFullYear() });
      await fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || t('operationFailed');
      if (detail.includes('future years')) {
        toast.error(t('cannotCreateFutureFees'));
      } else {
        toast.error(detail);
      }
    }
  };

  // Handle creating other payment schedule (Diğer Ödemeler)
  const handleCreateOtherPayment = async (e) => {
    e.preventDefault();
    try {
      if (!otherPaymentFormData.apartment_id) {
        toast.error(t('selectApartment'));
        return;
      }
      if (!otherPaymentFormData.description?.trim()) {
        toast.error(t('descriptionRequired'));
        return;
      }
      if (!otherPaymentFormData.amount_per_month || parseFloat(otherPaymentFormData.amount_per_month) <= 0) {
        toast.error(t('amountRequired'));
        return;
      }
      
      const url = buildingId 
        ? `${API}/other-payments/schedules?building_id=${buildingId}`
        : `${API}/other-payments/schedules`;
      
      await axios.post(url, {
        apartment_id: otherPaymentFormData.apartment_id,
        description: otherPaymentFormData.description.trim(),
        amount_per_month: parseFloat(otherPaymentFormData.amount_per_month),
        start_month: parseInt(otherPaymentFormData.start_month),
        start_year: parseInt(otherPaymentFormData.start_year),
        duration_months: parseInt(otherPaymentFormData.duration_months)
      }, { withCredentials: true });
      
      toast.success(t('otherPaymentCreated'));
      setShowOtherPaymentModal(false);
      setOtherPaymentFormData({
        apartment_id: '',
        description: '',
        amount_per_month: '',
        start_month: new Date().getMonth() + 1,
        start_year: new Date().getFullYear(),
        duration_months: 1
      });
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  if (loading || !filterApartment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Show empty state if no apartments exist
  if (apartments.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('maintenanceFees')}</h1>
            </div>
          </div>
        </header>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 text-center">
            <p className="text-gray-500">{t('noApartments')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('maintenanceFees')}</h1>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Initialize Carryover Button - only show if not initialized */}
              {!carryoverInitialized && filterApartment && (
                <button
                  data-testid="init-carryover-button"
                  onClick={() => {
                    setInitCarryoverData({ effective_year: currentYear, initial_amount: 0 });
                    setShowInitCarryoverModal(true);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors"
                >
                  {t('initializeCarryover')}
                </button>
              )}
              <button
                data-testid="generate-yearly-fees-button"
                onClick={() => setShowGenerateModal(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors"
              >
                {t('generateYearlyFees')}
              </button>
              <button
                data-testid="other-payments-button"
                onClick={() => setShowOtherPaymentModal(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors"
              >
                {t('addOtherPayment')}
              </button>
              <button
                data-testid="create-fee-button"
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors"
              >
                {t('newFee')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Filters - Only Apartment and Year */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-md p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">{t('apartment')}</label>
              <select
                value={filterApartment || ''}
                onChange={(e) => setFilterApartment(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                data-testid="filter-apartment"
              >
                {apartments
                  .slice()
                  .sort((a, b) => (parseInt(a.unit_number) || 0) - (parseInt(b.unit_number) || 0))
                  .map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {t('unitNumber')} {apt.unit_number}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">{t('year')}</label>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                data-testid="filter-year"
              >
                {Array.from({ length: 11 }, (_, i) => currentYear - 5 + i).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Main Table */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('period')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('aidatAmount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('otherPaymentAmount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('paidAmount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('cumulativeRemaining')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableRows.map((row) => {
                  const isDevir = row.isDevir;
                  
                  // Use pre-calculated values from useMemo
                  const { aidatDebt, otherPaymentDebt, totalMonthlyDebt, paymentsInMonth, calculatedCumulative } = row;
                  
                  // Other payments info for display
                  const hasOtherPayments = otherPaymentDebt > 0;
                  const otherPaymentsList = row.other_payments || [];
                  
                  // Cumulative balance color: negative (debt) = red, positive (credit) = green
                  const cumulativeColor = calculatedCumulative < -0.01 ? 'text-red-600' : calculatedCumulative > 0.01 ? 'text-green-600' : 'text-gray-600';
                  
                  // Row styling
                  let rowClass = '';
                  if (isDevir) {
                    rowClass = 'bg-purple-50';
                  } else if (hasOtherPayments) {
                    rowClass = 'bg-orange-50';
                  }
                  
                  return (
                    <tr key={row.id} data-testid={`fee-row-${row.id}`} className={rowClass}>
                      <td className="px-6 py-4 text-sm">
                        {isDevir ? (
                          <span className="font-semibold text-purple-700">{t('devirBakiyesi')}</span>
                        ) : (
                          <span>{t(`months.${row.month}`)} {row.year}</span>
                        )}
                      </td>
                      {/* Aidat (Maintenance Fee) Amount */}
                      <td className="px-6 py-4 text-sm font-semibold">
                        {isDevir ? '-' : (aidatDebt > 0 ? formatCurrency(aidatDebt) : '-')}
                      </td>
                      {/* Diğer Ödeme (Other Payment) Amount */}
                      <td className="px-6 py-4 text-sm font-semibold">
                        {isDevir ? '-' : (
                          otherPaymentDebt > 0 ? (
                            <div className="text-orange-600" title={otherPaymentsList.map(op => op.description).join(', ')}>
                              {formatCurrency(otherPaymentDebt)}
                              {otherPaymentsList.length > 0 && (
                                <span className="text-xs text-gray-500 block truncate max-w-[120px]">
                                  {otherPaymentsList.map(op => op.description).join(', ')}
                                </span>
                              )}
                            </div>
                          ) : '-'
                        )}
                      </td>
                      {/* Ödenen Tutar - Payments made IN this month (by payment_date) */}
                      <td className={`px-6 py-4 text-sm font-semibold ${paymentsInMonth > 0 ? 'text-green-600' : ''}`}>
                        {isDevir ? '-' : (paymentsInMonth > 0 ? formatCurrency(paymentsInMonth) : '-')}
                      </td>
                      {/* Kalan (Kümülatif) - Using new formula */}
                      <td className={`px-6 py-4 text-sm font-bold ${cumulativeColor}`}>
                        {formatCurrency(calculatedCumulative)}
                      </td>
                    </tr>
                  );
                })}
                {tableRows.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      {t('noRecords')}
                    </td>
                  </tr>
                )}
              </tbody>
              {/* Footer Totals Row */}
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td className="px-6 py-4 text-sm font-bold text-gray-700">{t('total') || 'TOPLAM'}</td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-800">{formatCurrency(summaryData.totalAidat)}</td>
                  <td className="px-6 py-4 text-sm font-bold text-orange-600">{formatCurrency(summaryData.totalDiger)}</td>
                  <td className="px-6 py-4 text-sm font-bold text-green-600">{formatCurrency(summaryData.totalOdenen)}</td>
                  <td className={`px-6 py-4 text-sm font-bold ${summaryData.endBalance < -0.01 ? 'text-red-600' : summaryData.endBalance > 0.01 ? 'text-green-600' : 'text-gray-600'}`}>
                    {formatCurrency(summaryData.endBalance)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Create Fee Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="create-fee-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('newFee')}</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('apartment')}</label>
                <select
                  data-testid="apartment-select"
                  value={formData.apartment_id}
                  onChange={(e) => setFormData({ ...formData, apartment_id: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="">{t('selectApartment')}</option>
                  {apartments.map((apt) => (
                    <option key={apt.id} value={apt.id}>{t('unitNumber')} {apt.unit_number}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('amount')}</label>
                <input
                  type="number"
                  data-testid="amount-input"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('month')}</label>
                  <input
                    type="number"
                    data-testid="month-input"
                    min="1"
                    max="12"
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('year')}</label>
                  <input
                    type="number"
                    data-testid="year-input"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border rounded-lg">{t('cancel')}</button>
                <button type="submit" data-testid="submit-fee" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg">{t('create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Yearly Fees Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="generate-yearly-fees-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('generateYearlyFees')}</h2>
            <form onSubmit={handleGenerateYearlyFees}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('apartment')}</label>
                <select
                  data-testid="generate-apartment-input"
                  value={generateFormData.apartment_id}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, apartment_id: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="">{t('selectApartment')}</option>
                  {apartments.map((apt) => (
                    <option key={apt.id} value={apt.id}>{t('unitNumber')} {apt.unit_number}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('year')}</label>
                <input
                  type="number"
                  data-testid="generate-year-input"
                  value={generateFormData.year}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, year: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  min="2020"
                  max={currentYear}
                />
                <p className="text-xs text-gray-500 mt-1">{t('cannotCreateFutureFees')}</p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('monthlyAmount')}</label>
                <input
                  type="number"
                  data-testid="generate-amount-input"
                  value={generateFormData.amount}
                  onChange={(e) => setGenerateFormData({ ...generateFormData, amount: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowGenerateModal(false)} className="flex-1 px-4 py-2 border rounded-lg">{t('cancel')}</button>
                <button type="submit" data-testid="submit-generate-yearly" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg">{t('generate')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Other Payments Modal (Diğer Ödemeler) */}
      {showOtherPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="other-payment-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('otherPaymentSchedule')}</h2>
            <form onSubmit={handleCreateOtherPayment}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('apartment')}</label>
                <select
                  data-testid="other-payment-apartment-select"
                  value={otherPaymentFormData.apartment_id}
                  onChange={(e) => setOtherPaymentFormData({ ...otherPaymentFormData, apartment_id: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="">{t('selectApartment')}</option>
                  {apartments.map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {apt.unit_number} - {apt.owner_name || apt.resident_name || '-'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('startMonth')}</label>
                  <select
                    data-testid="other-payment-start-month"
                    value={otherPaymentFormData.start_month}
                    onChange={(e) => setOtherPaymentFormData({ ...otherPaymentFormData, start_month: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <option key={m} value={m}>{t(`months.${m}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('year')}</label>
                  <select
                    data-testid="other-payment-start-year"
                    value={otherPaymentFormData.start_year}
                    onChange={(e) => setOtherPaymentFormData({ ...otherPaymentFormData, start_year: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  >
                    {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('durationMonths')}</label>
                <input
                  type="number"
                  data-testid="other-payment-duration"
                  value={otherPaymentFormData.duration_months}
                  onChange={(e) => setOtherPaymentFormData({ ...otherPaymentFormData, duration_months: Math.max(1, parseInt(e.target.value) || 1) })}
                  min="1"
                  max="60"
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('amountPerMonth')}</label>
                <input
                  type="number"
                  step="0.01"
                  data-testid="other-payment-amount"
                  value={otherPaymentFormData.amount_per_month}
                  onChange={(e) => setOtherPaymentFormData({ ...otherPaymentFormData, amount_per_month: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('paymentDescription')}</label>
                <textarea
                  data-testid="other-payment-description"
                  value={otherPaymentFormData.description}
                  onChange={(e) => setOtherPaymentFormData({ ...otherPaymentFormData, description: e.target.value })}
                  placeholder={t('paymentDescriptionPlaceholder')}
                  rows="2"
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => setShowOtherPaymentModal(false)} 
                  className="flex-1 px-4 py-2 border rounded-lg"
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  data-testid="submit-other-payment" 
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  {t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Simplified Payment Modal - Only essential fields */}
      {showPaymentModal && selectedFee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="payment-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('markAsPaid')}</h2>
            
            {/* Simplified Fee Info - Only apartment and period (read-only) */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{t('apartment')}:</span>
                <span className="font-semibold">{selectedFee.apartment_unit}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{t('period')}:</span>
                <span className="font-semibold">{t(`months.${selectedFee.month}`)} {selectedFee.year}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-600 font-semibold">{t('remainingAmount')}:</span>
                <span className="font-bold text-red-600">{formatCurrency(selectedFee.amount - (selectedFee.paid_amount_total || 0))}</span>
              </div>
            </div>

            <form onSubmit={handlePaymentSubmit}>
              {/* Payment Amount */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('paymentAmount')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedFee.amount - (selectedFee.paid_amount_total || 0)}
                  value={paymentFormData.amount}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  data-testid="payment-amount-input"
                  required
                />
              </div>
              
              {/* Payment Date */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('paymentDate')}</label>
                <input
                  type="date"
                  value={paymentFormData.payment_date}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="payment-date-input"
                  required
                />
              </div>

              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedFee(null);
                  }} 
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  data-testid="confirm-payment"
                >
                  {t('confirmPayment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Initialize Carryover Modal (ONE-TIME) */}
      {showInitCarryoverModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="init-carryover-modal">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('initializeCarryover')}</h2>
            
            {/* Help text */}
            <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">{t('initializeCarryoverHelp')}</p>
              <p className="text-xs text-purple-600 mt-2 font-semibold">{t('positiveIsDebt')}</p>
            </div>
            
            {/* Warning */}
            <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700 font-semibold">⚠️ {t('immutableWarning')}</p>
            </div>

            <form onSubmit={handleInitCarryover}>
              {/* Apartment (read-only) */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('apartment')}</label>
                <input
                  type="text"
                  value={apartments.find(a => a.id === filterApartment)?.unit_number || filterApartment}
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100"
                  disabled
                />
              </div>
              
              {/* Effective Year */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('effectiveYear')}</label>
                <input
                  type="number"
                  value={initCarryoverData.effective_year}
                  onChange={(e) => setInitCarryoverData({ ...initCarryoverData, effective_year: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="init-carryover-year"
                  required
                  min="2020"
                  max="2030"
                />
              </div>
              
              {/* Initial Amount */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('initialCarryoverAmount')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={initCarryoverData.initial_amount}
                  onChange={(e) => setInitCarryoverData({ ...initCarryoverData, initial_amount: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  data-testid="init-carryover-amount"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{t('positiveIsDebt')}</p>
              </div>

              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => setShowInitCarryoverModal(false)} 
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  data-testid="confirm-init-carryover"
                >
                  {t('confirm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
